import { useTranslation } from 'react-i18next';
import { Field } from './Field.js';
import { ColorField } from './ColorField.js';
import { SegmentedControl } from './SegmentedControl.js';
import { isTransparentBg, FORM_BG_TRANSPARENT } from '@evo-hub/shared';

// ── Selector de fondo de una capa ────────────────────────────────────────────
// Promovido desde FormDetailPage (2026-09-01, REGLA #13) al centralizarse la
// marca en Configuración: el panel de Apariencia del formulario desapareció y
// este control pasó a vivir en la sección de marca de la cuenta. Se movió tal
// cual — está bien pensado y reescribirlo habría sido perder el razonamiento
// que tiene adentro.

/** Selector de fondo de una capa: default / color / transparente.
 *
 *  Un solo componente para las dos capas (tarjeta y fondo alrededor): si el
 *  selector viviera dos veces, la próxima opción se agregaría en una sola.
 *
 *  `defaultLabel` nombra el valor de fábrica de ESA capa ("Blanco" para la
 *  tarjeta, "Gris claro" para el fondo alrededor). Sin eso, dos controles
 *  idénticos que dicen "Por defecto" no le dicen a nadie qué van a hacer. */
export function BackgroundField({ label, hint, value, fallback, defaultLabel, onChange }: {
  label: string;
  hint: string;
  value: string | null;
  fallback: string;
  defaultLabel: string;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const transparente = isTransparentBg(value);
  const modo: 'default' | 'color' | 'transparent' = transparente ? 'transparent' : (value ? 'color' : 'default');

  return (
    <Field label={label} hint={hint}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        <SegmentedControl<'default' | 'color' | 'transparent'>
          value={modo}
          onChange={m => {
            if (m === 'default') onChange(null);
            else if (m === 'transparent') onChange(FORM_BG_TRANSPARENT);
            // 'color' arranca del valor de fábrica de esa capa: pasar a elegir
            // color no puede cambiar el aspecto por sí solo, solo habilita el
            // selector.
            else onChange(fallback);
          }}
          options={[
            { value: 'default', label: defaultLabel },
            { value: 'color', label: t('forms.appearance.bgColor') },
            { value: 'transparent', label: t('forms.appearance.bgTransparent') },
          ]}
        />
        {modo === 'color' && (
          <ColorField
            label={t('forms.appearance.bgColorLabel')}
            hint={t('forms.appearance.bgColorHint')}
            value={value}
            fallback={fallback}
            onCommit={hex => onChange(hex)}
            onReset={() => onChange(null)}
          />
        )}
      </div>
    </Field>
  );
}
